import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Check, AlertCircle, RefreshCw, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { boPath } from '@/utils/getBasePath';
import { ASTERPOS_LOGO } from '@/config/brandAssets';

const API = process.env.REACT_APP_BACKEND_URL || '';
const TRIAL_DAYS = 7;   // the website's free trial
const PLAN_KEY = 'chain29_signup_plan';

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('backoffice_token')}`,
});
const storedPlan = () => {
  try { return localStorage.getItem(PLAN_KEY); } catch { return null; }
};
const rememberPlan = (code) => {
  try { if (code) localStorage.setItem(PLAN_KEY, code); else localStorage.removeItem(PLAN_KEY); } catch { /* private window */ }
};

/**
 * The last step of sign-up (owner, Nov 5 2026: "user must save card for billing at sign up"):
 * pick a plan, save the billing card on the secure card page (the company merchant account,
 * through the payment hub), and the free trial starts. Nothing is charged today; autopay
 * charges the card one day before the trial ends.
 */
export default function BillingSetupPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [noAccess, setNoAccess] = useState(false);

  const startTrial = useCallback(async (code) => {
    setBusy('trial');
    try {
      const res = await fetch(`${API}/api/billing/v2/subscription/start`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ plan_code: code, trial_days: TRIAL_DAYS }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not start your plan');
      rememberPlan(null);
      toast.success(`Your ${TRIAL_DAYS}-day free trial has started`);
      navigate(boPath(), { replace: true });
    } catch (e) {
      setError(e.message);
      setBusy('');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    const [pr, sr] = await Promise.all([
      fetch(`${API}/api/billing/v2/plans`, { headers: headers() }),
      fetch(`${API}/api/billing/v2/me/subscription`, { headers: headers() }),
    ]);
    if (sr.status === 401) { navigate('/owner/login', { replace: true }); return null; }
    if (sr.status === 403) { setNoAccess(true); return null; }
    const p = await pr.json().catch(() => ({}));
    const s = await sr.json().catch(() => ({}));
    if (!sr.ok) throw new Error(s.detail || 'Could not load billing');
    const list = (p.plans || []).filter((x) => x.is_active !== false);
    setPlans(list);
    setStatus(s);
    // a plan is on already, or the company billing account is not connected on this server
    // (no card can be saved yet): straight to the back office
    if (['trialing', 'active', 'past_due'].includes(s.status) || s.autopay_connected === false) {
      navigate(boPath(), { replace: true });
      return null;
    }
    const want = new URLSearchParams(window.location.search).get('plan') || storedPlan();
    const pick = list.find((x) => x.code === (want || '').toUpperCase()) || list.find((x) => x.code === 'GROWTH') || list[0];
    setSelected(pick ? pick.code : null);
    return { status: s, plan: pick };
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('backoffice_token')) { navigate('/owner/login', { replace: true }); return; }
    const params = new URLSearchParams(window.location.search);
    const back = params.get('card') === 'saved';
    if (back) {
      params.delete('card');
      const rest = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    (async () => {
      try {
        if (back) {
          // back from the card page: read the card it saved, then start the trial
          setBusy('sync');
          const r = await fetch(`${API}/api/billing/v2/payment-methods/sync`, { method: 'POST', headers: headers() });
          const d = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(d.detail || 'Could not read the saved card');
          setBusy('');
        }
        const got = await load();
        if (back && got && got.status.has_card && got.plan) await startTrial(got.plan.code);
        else if (back && got && !got.status.has_card) setError('No card was saved. Add your card to finish signing up.');
      } catch (e) {
        setBusy('');
        setError(e.message);
      }
    })();
  }, [load, startTrial, navigate]);

  const addCard = async () => {
    if (!selected) return;
    setError('');
    setBusy('card');
    rememberPlan(selected);
    try {
      const res = await fetch(`${API}/api/billing/v2/payment-methods/setup-intent`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ return_url: `${window.location.origin}/owner/signup/billing?card=saved&plan=${selected}` }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.hpp_url) throw new Error(data.detail || 'The secure card page is not available right now');
      window.location.href = data.hpp_url;
    } catch (e) {
      setError(e.message);
      setBusy('');
    }
  };

  const signOut = () => {
    localStorage.removeItem('backoffice_token');
    localStorage.removeItem('backoffice_active_restaurant');
    navigate('/owner/login', { replace: true });
  };

  const plan = plans.find((p) => p.code === selected);
  const hasCard = !!status?.has_card;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8" data-testid="billing-setup-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center mb-6">
          <img src={ASTERPOS_LOGO} alt="" className="h-10 w-auto" />
        </div>
        <Card className="bg-white/10 backdrop-blur-sm border-white/20">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-xl sm:text-2xl text-white">Last step: your plan and billing card</CardTitle>
            <CardDescription className="text-sm sm:text-base text-slate-300">
              Your restaurant is created. Pick a plan and save a card for billing to finish signing up.
              Nothing is charged today.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-5">
            {noAccess && (
              <div className="p-4 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-100 text-sm" data-testid="billing-setup-owner-only">
                The restaurant owner needs to finish billing setup before the back office opens.
                <div className="mt-3"><Button variant="outline" onClick={signOut} className="min-h-[44px]">Sign out</Button></div>
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-100" data-testid="billing-setup-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {!noAccess && !plans.length && !error && (
              <p className="text-slate-300 text-sm py-6 text-center"><RefreshCw className="inline h-4 w-4 mr-2 animate-spin" />Loading...</p>
            )}
            {!noAccess && plans.length > 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {plans.map((p) => (
                    <button
                      type="button"
                      key={p.code}
                      onClick={() => setSelected(p.code)}
                      className={`text-left rounded-lg border-2 p-4 transition-colors min-h-[44px] ${
                        selected === p.code ? 'border-orange-500 bg-orange-500/10' : 'border-white/15 bg-white/5 hover:border-white/30'
                      }`}
                      data-testid={`setup-plan-${p.code}`}
                      aria-pressed={selected === p.code}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-white">{p.name}</span>
                        {p.code === 'GROWTH' && <span className="text-[11px] rounded bg-orange-500 text-white px-2 py-0.5">Most popular</span>}
                      </div>
                      <p className="text-2xl font-bold text-white mt-1">{money(p.price_cents)}<span className="text-sm font-normal text-slate-400">/month</span></p>
                      <p className="text-xs text-slate-300 mt-2">
                        {(p.sms_included || 0).toLocaleString()} texts and {(p.ai_included || 0).toLocaleString()} AI requests a month included
                      </p>
                    </button>
                  ))}
                </div>
                {plan && (
                  <ul className="text-sm text-slate-200 space-y-1.5" data-testid="billing-setup-terms">
                    <li className="flex gap-2"><Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />{TRIAL_DAYS}-day free trial of {plan.name}. Nothing is charged today.</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />Autopay charges this card {money(plan.price_cents)} one day before the trial ends, then monthly.</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Over the plan: {money(plan.sms_overage_cents)} per text and {money(plan.ai_overage_cents)} per AI request, itemized on your invoice.
                    </li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />Change plans or cancel any time in Back Office, Billing.</li>
                  </ul>
                )}
                <Button
                  onClick={hasCard ? () => startTrial(selected) : addCard}
                  disabled={!selected || !!busy}
                  className="w-full min-h-[48px] bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                  data-testid="billing-setup-continue"
                >
                  {busy ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                  {busy === 'sync' ? 'Reading your card...' : busy === 'trial' ? 'Starting your trial...'
                    : hasCard ? `Start ${TRIAL_DAYS}-day free trial` : `Add billing card and start ${TRIAL_DAYS}-day free trial`}
                </Button>
                <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <Lock className="w-3.5 h-3.5" />Your card is entered on our secure card page and never stored by the restaurant app.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
